/**
 * ChatKit Widget
 *
 * Chat widget system for Juno supporting OpenAI ChatKit and AWS Connect live agent.
 *
 * Structure:
 * - index.js          - Entry point with initApps-compatible ChatKit class
 * - chatkit.css       - Common widget styles (toggle, header, window)
 *
 * - common/
 *   - widgetManager.js       - Shared orchestration (toggle, widget tracking, swap logic)
 *   - helpers.js             - Utility functions (data attribute parsing)
 *   - conversationChannel.js - ActionCable subscription for chatkit conversation events
 *
 * - openai/
 *   - openaiWidget.js   - OpenAI ChatKit initialization, config, destroy
 *   - configs.js        - Widget type configurations (prompts, placeholders, etc.)
 *   - scriptLoader.js   - OpenAI CDN script loader
 *   - apiRoutes.js      - ChatKit API routes and token fetching
 *
 * - aws/
 *   - awsWidget.js       - AWS Connect widget initialization and destroy
 *   - awsWidget.css      - AWS Connect widget styles (messages, bubbles)
 *   - liveAgentEvents.js - WebSocket message processing
 *   - websocketClient.js - WebSocket connection management
 *
 * Available Widget Types (see openai/configs.js):
 * - channel_admin: Default for admin users, full-featured assistant
 * - employee_import: For employee import flow with preview_id context
 *
 * Usage:
 *
 * 1. Global Widget (toggle button in corner):
 *    Rendered via `layouts/shared/_chatkit.html.slim`.
 *    Container ID: `chatkit-widget-container`
 *    Hide with: `content_for :hide_chatkit_toggle`
 *
 * 2. Inline Widget (embedded in page):
 *    Container ID must end with `-chat-widget` for auto-initialization.
 *
 *    ```slim
 *    #employee-import-chat-widget[
 *      data-chatkit-type="employee_import"
 *      data-chatkit-preview-id=@preview.id
 *    ]
 *      openai-chatkit
 *    ```
 *
 * Context from data attributes:
 * All `data-chatkit-*` attributes (except `data-chatkit-type`) are passed to backend:
 * - `data-chatkit-preview-id="123"` -> `{ preview_id: "123" }`
 * - `data-chatkit-group-id="456"` -> `{ group_id: "456" }`
 *
 * Adding New Widget Types:
 *
 * 1. Add configuration to `openai/configs.js`:
 *    ```js
 *    my_new_type: {
 *      context: { flow: 'my_new_type' },
 *      placeholder: 'Ask me anything...',
 *      attachments: { enabled: true, maxCount: 5, maxSize: 10485760 },
 *      greeting: 'Hello! How can I help?',
 *      prompts: [
 *        { label: 'Button text', prompt: 'Prompt sent to AI' }
 *      ]
 *    }
 *    ```
 *
 * 2. Use in HTML (ID must end with `-chat-widget`):
 *    ```slim
 *    #my-new-type-chat-widget[data-chatkit-type="my_new_type"]
 *      openai-chatkit
 *    ```
 */
import { setupWidgetButtons, destroyWidgets, cleanupWidgetButtons } from './common/widgetManager';
import { activate as activateOpenAIWidget } from './openai/openaiWidget';
import './chatkit.css';

export default class ChatKit {
  init() {
    if (document.getElementById('chatkit-widget-container')) {
      setupWidgetButtons();
    }
    const inlineWidget = document.querySelector('[id$="-chat-widget"][data-chatkit-type]');
    if (inlineWidget) {
      activateOpenAIWidget(null, `#${inlineWidget.id}`);
    }
  }
}

document.addEventListener('turbo:before-cache', () => {
  destroyWidgets();
  cleanupWidgetButtons();
});
